import { Image } from 'shared/components/Image';
import { normalizeValue } from 'shared/utils/normalizeValue';
import styled, { css } from 'styled-components';

interface IGridCellProductImage {
  disableLazyLoad?: boolean;
  imageSrc: string;
  isActiveCell: boolean;
  isSellerImage: boolean;
  name: string;
  productType?: string;
}

export const GridCellProductImage = ({
  disableLazyLoad = false,
  imageSrc,
  isActiveCell,
  isSellerImage,
  name,
  productType,
}: IGridCellProductImage) => {
  const isPlaceholderImage = imageSrc?.includes('/placeholders/');
  const animateCrop = !isPlaceholderImage && !isSellerImage;

  return (
    <GridCellProductImage.ImageWrapper $productType={productType} $isSellerImage={isSellerImage}>
      <GridCellProductImage.Image
        alt={normalizeValue(name)}
        disableLazyLoad={disableLazyLoad}
        crop
        data-qa="grid_cell_product_image"
        $animateCrop={animateCrop}
        $isSellerImage={isSellerImage}
        $isActiveCell={isActiveCell}
        placeholderStyles={GridCellProductImage.placeholderStyles}
        $productType={productType}
        // 18.75 comes from 75 divided by 4, where 75 is the width percentage of the search grid
        // and 4 is the amount of columns within the grid (75/4 = 18.75)
        // 48px is the sum of the padding left & padding right on the image inside the grid (24 + 24 = 48).
        sizes="(min-width: 768px) calc(18.75vw - 48px), calc(50vw - 48px)"
        src={imageSrc}
      />
    </GridCellProductImage.ImageWrapper>
  );
};

GridCellProductImage.ImageWrapper = styled.div<{
  $productType?: string;
  $isSellerImage: boolean;
}>`
  padding-bottom: ${({ $isSellerImage }) => ($isSellerImage ? '115%' : '89%')};
  position: relative;
  width: 100%;

  /* styling the loading indicator*/
  .lazyload-wrapper > div {
    position: absolute;
    padding: 24px;
  }

  ${({ $productType, $isSellerImage }) => {
    if ($productType === 'tops') {
      return css`
        margin-top: ${$isSellerImage ? '0' : '45px'};
      `;
    }
    if ($productType === 'dresses') {
      return css`
        margin-top: ${$isSellerImage ? '0' : '65px'};
      `;
    }
    return css`
      margin-top: ${$isSellerImage ? '0' : '47px'};
    `;
  }};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${({ $productType, $isSellerImage }) => {
      if ($productType === 'tops') {
        return css`
          margin-top: ${$isSellerImage ? '0' : '45px'};
        `;
      }
      if ($productType === 'dresses') {
        return css`
          margin-top: ${$isSellerImage ? '0' : '65px'};
        `;
      }
      return css`
        margin-top: ${$isSellerImage ? '0' : '76px'};
      `;
    }}
  }
`;

const cropAnimation = css<{ $isActiveCell: boolean }>`
  @media (hover: hover) and (pointer: fine) {
    object-fit: ${({ $isActiveCell }) => ($isActiveCell ? 'contain' : 'cover')} !important;
    object-position: ${({ $isActiveCell }) => ($isActiveCell ? 'bottom' : 'top center')};
  }
`;

GridCellProductImage.Image = styled(Image)<{
  $animateCrop: boolean;
  $isActiveCell: boolean;
  $isSellerImage: boolean;
  $productType?: string;
}>`
  height: 100%;
  width: 100%;
  object-fit: ${({ $isSellerImage }) => ($isSellerImage ? 'cover' : 'contain')} !important;
  object-position: bottom;
  padding: 0 24px 24px;
  transform: ${({ $isSellerImage }) => $isSellerImage && 'scale(0.85)'};
  transform-origin: ${({ $isSellerImage }) => $isSellerImage && 'bottom'};
  position: absolute;
  /* center absolutely positioned div */
  ${({ $isSellerImage }) =>
    !$isSellerImage &&
    css`
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    `};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${({ $productType, $animateCrop, $isActiveCell, $isSellerImage }) => {
      if ($isSellerImage) {
        return css`
          padding: 0 24px 24px;
          left: 0;
          top: 0;
          /* commenting this out as well as design is unsure if they want to remove yet */
          // transform: ${$isActiveCell ? 'scale(0.70)' : 'scale(0.85)'};
          //transform-origin: bottom;
        `;
      }
      if ($productType === 'tops' || $productType === 'outerwear') {
        return css`
          padding: 0 24px 24px;
          width: auto !important;
          ${$animateCrop && cropAnimation};
        `;
      }
      if ($productType === 'dresses') {
        return css`
          padding: 0 40px 0;
          ${$animateCrop &&
          css`
            padding: ${$isActiveCell && '0 40px 24px'};
            ${cropAnimation};
          `}
        `;
      }
      return css`
        padding: 0 32px 24px;
      `;
    }}
  }
`;

GridCellProductImage.placeholderStyles = css`
  background-position: bottom 0px right 50% !important;
`;
